import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Search, FileSpreadsheet, Download, X, Trash2, CreditCard, MessageSquare } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { StatusBadge } from "@/components/StatusBadge";
import { ColorDot, COLOR_BG } from "@/components/ColorDot";
import { formatDate } from "@/utils/formatDate";

const API  = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;

const ALL_STATUSES = [
  "Received","Under Investigation","Waiting for Approval",
  "Repair in Progress","Repaired","Replacement Approved",
  "Replacement Dispatched","Completed","Warranty Rejected",
];
const WARRANTY_STATUSES = ["Under warranty","Out of warranty","Open","Damage"];

const AVATAR_COLORS = [
  "bg-lime-600","bg-violet-500","bg-emerald-500",
  "bg-amber-500","bg-rose-500","bg-cyan-600",
];

function getInitials(name = "") {
  return name.trim().split(/\s+/).map((w) => w[0] || "").join("").slice(0, 2).toUpperCase() || "?";
}

function warrantyClass(ws) {
  if (ws === "Out of warranty") return "bg-red-50 text-red-700 border-red-100";
  if (ws === "Under warranty") return "bg-lime-50 text-lime-700 border-lime-100";
  if (ws === "Damage") return "bg-orange-50 text-orange-700 border-orange-100";
  return "bg-gray-100 text-gray-600 border-gray-200";
}

// ─── Date grouping ────────────────────────────────────────────────────────────
function getGroupLabel(dateStr) {
  if (!dateStr) return "No Date";
  const today = new Date(); today.setHours(0,0,0,0);
  const yest  = new Date(today); yest.setDate(yest.getDate() - 1);
  if (dateStr === today.toISOString().split("T")[0]) return "Today";
  if (dateStr === yest.toISOString().split("T")[0])  return "Yesterday";
  const d = new Date(dateStr + "T00:00:00");
  return d.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
}

function groupByDate(tickets) {
  const groups = {};
  const order  = [];
  tickets.forEach((t) => {
    const label = getGroupLabel(t.received_date);
    if (!groups[label]) { groups[label] = []; order.push({ label, date: t.received_date || "" }); }
    groups[label].push(t);
  });
  order.sort((a, b) => {
    if (a.label === "Today")     return -1; if (b.label === "Today")     return 1;
    if (a.label === "Yesterday") return -1; if (b.label === "Yesterday") return 1;
    return (b.date || "").localeCompare(a.date || "");
  });
  return order.map(({ label }) => ({ label, tickets: groups[label] }));
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function TicketList() {
  const [tickets,       setTickets]       = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [search,        setSearch]        = useState("");
  const [statusFilter,  setStatusFilter]  = useState("all");
  const [warrantyFilter,setWarrantyFilter]= useState("all");
  const [selected,      setSelected]      = useState(new Set());
  const [bulkStatus,    setBulkStatus]    = useState("");
  const [bulkLoading,   setBulkLoading]   = useState(false);
  const navigate = useNavigate();

  const fetchTickets = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter !== "all") params.status = statusFilter;
      if (warrantyFilter !== "all") params.warranty_status = warrantyFilter;
      if (search.trim()) params.search = search.trim();
      const r = await axios.get(`${API}/tickets`, { params, withCredentials: true });
      setTickets(r.data);
    } catch { toast.error("Failed to load tickets"); }
    finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, statusFilter, warrantyFilter]);

  useEffect(() => {
    const t = setTimeout(fetchTickets, 300);
    return () => clearTimeout(t);
  }, [fetchTickets]);

  // ─── Color tag ───────────────────────────────────────────────────────────────
  const handleColorTag = useCallback(async (ticketId, color) => {
    setTickets((prev) => prev.map((t) => t._id === ticketId ? { ...t, color_tag: color } : t));
    try {
      await axios.put(`${API}/tickets/${ticketId}`, { color_tag: color }, { withCredentials: true });
    } catch {
      toast.error("Failed to save color");
      setTickets((prev) => prev.map((t) => t._id === ticketId ? { ...t, color_tag: undefined } : t));
    }
  }, []);

  // ─── Selection ───────────────────────────────────────────────────────────────
  const toggleSelect = (id, e) => {
    e.stopPropagation();
    setSelected((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  };

  const toggleAll = () => {
    if (selected.size === tickets.length && tickets.length > 0) setSelected(new Set());
    else setSelected(new Set(tickets.map((t) => t._id)));
  };

  const handleBulkStatus = async () => {
    if (!bulkStatus || selected.size === 0) return;
    setBulkLoading(true);
    try {
      const { data } = await axios.post(`${API}/tickets/bulk-status`,
        { ticket_ids: [...selected], status: bulkStatus }, { withCredentials: true });
      toast.success(`Updated ${data.updated} ticket${data.updated !== 1 ? "s" : ""}`);
      setSelected(new Set()); setBulkStatus(""); fetchTickets();
    } catch { toast.error("Bulk update failed"); }
    finally { setBulkLoading(false); }
  };

  const handleBulkDelete = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(`Delete ${selected.size} ticket(s)? This cannot be undone.`)) return;
    setBulkLoading(true);
    try {
      const { data } = await axios.post(`${API}/tickets/bulk-delete`,
        { ticket_ids: [...selected] }, { withCredentials: true });
      toast.success(`Deleted ${data.deleted} ticket${data.deleted !== 1 ? "s" : ""}`);
      setSelected(new Set()); fetchTickets();
    } catch { toast.error("Bulk delete failed"); }
    finally { setBulkLoading(false); }
  };

  const handleExportSelected = async () => {
    if (selected.size === 0) return;
    try {
      const r = await axios.post(`${API}/export/excel-selected`,
        { ticket_ids: [...selected] }, { withCredentials: true, responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url; a.download = "selected_tickets.xlsx"; a.click();
      URL.revokeObjectURL(url);
      toast.success(`Exported ${selected.size} ticket${selected.size !== 1 ? "s" : ""}`);
    } catch { toast.error("Export failed"); }
  };

  const handleBulkPayment = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(`Mark ${selected.size} ticket(s) as Paid?`)) return;
    setBulkLoading(true);
    try {
      const { data } = await axios.post(`${API}/tickets/bulk-payment`,
        { ticket_ids: [...selected] }, { withCredentials: true });
      toast.success(`Marked ${data.updated} ticket${data.updated !== 1 ? "s" : ""} as Paid`);
      setSelected(new Set()); fetchTickets();
    } catch { toast.error("Bulk payment update failed"); }
    finally { setBulkLoading(false); }
  };

  const downloadBlob = async (url, filename) => {
    try {
      const r = await axios.get(url, { withCredentials: true, responseType: "blob" });
      const bUrl = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = bUrl; a.download = filename; a.click();
      URL.revokeObjectURL(bUrl);
    } catch { toast.error("Export failed"); }
  };

  const allSelected  = tickets.length > 0 && selected.size === tickets.length;
  const someSelected = selected.size > 0 && !allSelected;
  const groups       = groupByDate(tickets);

  return (
    <div className="min-h-screen bg-gray-50" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-xl font-bold text-gray-900 tracking-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Service Tickets
            </h1>
            <p className="text-xs text-gray-400 mt-0.5">{tickets.length} total records</p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button variant="outline" size="sm"
              onClick={() => downloadBlob(`${BACKEND}/api/export/excel`, "service_tickets.xlsx")}
              data-testid="export-excel-btn" className="text-gray-600 border-gray-200 hover:bg-gray-50">
              <FileSpreadsheet className="w-4 h-4 mr-1.5" /> Export Excel
            </Button>
            <Button variant="outline" size="sm"
              onClick={() => downloadBlob(`${BACKEND}/api/export/csv`, "service_tickets.csv")}
              data-testid="export-csv-btn" className="text-gray-600 border-gray-200 hover:bg-gray-50">
              <Download className="w-4 h-4 mr-1.5" /> Export CSV
            </Button>
            <Button onClick={() => navigate("/tickets/new")} data-testid="new-ticket-btn"
              className="bg-lime-600 hover:bg-lime-700 text-white shadow-sm shadow-lime-900/20">
              <Plus className="w-4 h-4 mr-1.5" /> New Ticket
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-6">
        {/* Filters */}
        <div className="bg-white rounded-2xl border border-gray-200 p-4 mb-4 shadow-sm">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input value={search} onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by name, phone, ticket ID, serial no..."
                className="pl-9 border-gray-200 focus:ring-lime-500"
                data-testid="search-input" />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full sm:w-52 border-gray-200" data-testid="status-filter-trigger">
                <SelectValue placeholder="All Statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                {ALL_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={warrantyFilter} onValueChange={setWarrantyFilter}>
              <SelectTrigger className="w-full sm:w-44 border-gray-200" data-testid="warranty-filter-trigger">
                <SelectValue placeholder="All Warranty" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Warranty</SelectItem>
                {WARRANTY_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden shadow-sm">
          {loading ? (
            <div className="p-6 space-y-3">
              {[...Array(8)].map((_, i) => <div key={i} className="h-14 bg-gray-50 rounded-xl animate-pulse" />)}
            </div>
          ) : tickets.length === 0 ? (
            <div className="text-center py-16 text-gray-400 text-sm">No tickets found.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    {/* Checkbox */}
                    <th className="px-4 py-3 w-10">
                      <button onClick={toggleAll} data-testid="select-all-checkbox"
                        className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all ${
                          allSelected ? "bg-lime-600 border-lime-600"
                          : someSelected ? "bg-lime-100 border-lime-400"
                          : "border-gray-300 hover:border-lime-400"
                        }`}>
                        {allSelected && <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>}
                        {someSelected && <div className="w-2 h-0.5 bg-lime-600 rounded" />}
                      </button>
                    </th>
                    {/* Color */}
                    <th className="px-2 py-3 w-8" title="Color tag" />
                    {["Customer","Ticket ID","Charger","Warranty","Received","Status",""].map((h) => (
                      <th key={h} className="text-left px-4 py-3 text-xs font-bold uppercase tracking-widest text-gray-400">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {groups.map(({ label, tickets: groupTickets }) => (
                    <>
                      {/* Date section header */}
                      <tr key={`hdr-${label}`}>
                        <td colSpan={9} className="px-5 pt-5 pb-2 bg-gray-50/80 border-b border-gray-100">
                          <div className="flex items-center gap-3">
                            <span className="text-xs font-black uppercase tracking-widest text-gray-600">{label}</span>
                            <div className="flex-1 h-px bg-gray-200" />
                            <span className="text-[10px] font-bold text-gray-400">
                              {groupTickets.length} ticket{groupTickets.length !== 1 ? "s" : ""}
                            </span>
                          </div>
                        </td>
                      </tr>
                      {/* Ticket rows */}
                      {groupTickets.map((t, i) => {
                        const isSel = selected.has(t._id);
                        return (
                          <tr key={t._id} data-testid={`ticket-row-${i}`}
                            onClick={() => navigate(`/tickets/${t._id}`)}
                            className={`border-b border-gray-50 cursor-pointer transition-colors group ${
                              isSel ? "bg-lime-50/60 hover:bg-lime-50"
                              : t.color_tag ? "hover:bg-gray-50/80"
                              : "hover:bg-gray-50/80"
                            }`}
                            style={t.color_tag ? { borderLeft: `3px solid var(--color-${t.color_tag}, #e5e7eb)` } : {}}
                          >
                            {/* Checkbox */}
                            <td className="px-4 py-3" onClick={(e) => toggleSelect(t._id, e)}>
                              <button data-testid={`select-ticket-${i}`}
                                className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all ${
                                  isSel ? "bg-lime-600 border-lime-600" : "border-gray-300 hover:border-lime-400"
                                }`}>
                                {isSel && <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>}
                              </button>
                            </td>
                            {/* Color dot + notes badge */}
                            <td className="px-2 py-3" onClick={(e) => e.stopPropagation()}>
                              <div className="flex items-center gap-1">
                                <ColorDot
                                  color={t.color_tag}
                                  onColorChange={(color) => handleColorTag(t._id, color)}
                                />
                                {(t.notes?.length || 0) > 0 && (
                                  <div className="flex items-center gap-0.5 bg-blue-50 border border-blue-200 text-blue-600 rounded-full px-1.5 py-0.5" title={`${t.notes.length} note${t.notes.length !== 1 ? "s" : ""}`}>
                                    <MessageSquare className="w-2.5 h-2.5" />
                                    <span className="text-[9px] font-bold leading-none">{t.notes.length}</span>
                                  </div>
                                )}
                              </div>
                            </td>
                            {/* Customer */}
                            <td className="px-4 py-3.5">
                              <div className="flex items-center gap-3">
                                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0 ${AVATAR_COLORS[i % AVATAR_COLORS.length]}`}>
                                  {getInitials(t.customer_name)}
                                </div>
                                <div>
                                  <p className="font-semibold text-gray-900 text-sm leading-tight">{t.customer_name}</p>
                                  <p className="text-xs text-gray-400">{t.phone}</p>
                                </div>
                              </div>
                            </td>
                            {/* Ticket ID */}
                            <td className="px-4 py-3.5">
                              <span className="font-mono text-xs font-bold text-lime-700 bg-lime-50 px-2.5 py-1 rounded-lg border border-lime-100">
                                {t.ticket_id}
                              </span>
                            </td>
                            {/* Charger */}
                            <td className="px-4 py-3.5 text-sm text-gray-600">
                              {t.chargers?.[0]?.charger_model || t.charger_model || "—"}
                              {(t.chargers?.length || 0) > 1 && <span className="ml-1 text-xs text-gray-400">+{t.chargers.length - 1}</span>}
                            </td>
                            {/* Warranty */}
                            <td className="px-4 py-3.5">
                              <span className={`text-xs px-2 py-0.5 rounded-full font-bold border ${warrantyClass(t.warranty_status || t.chargers?.[0]?.warranty_status)}`}>
                                {t.warranty_status || t.chargers?.[0]?.warranty_status || "—"}
                              </span>
                            </td>
                            {/* Received */}
                            <td className="px-4 py-3.5 text-sm text-gray-500 whitespace-nowrap">{formatDate(t.received_date)}</td>
                            {/* Status */}
                            <td className="px-4 py-3.5"><StatusBadge status={t.status} /></td>
                            {/* Actions */}
                            <td className="px-4 py-3.5 text-right">
                              <button data-testid={`view-ticket-${i}`}
                                onClick={(e) => { e.stopPropagation(); navigate(`/tickets/${t._id}`); }}
                                className="text-lime-600 hover:text-lime-800 text-xs font-bold px-2.5 py-1 rounded-lg hover:bg-lime-50 transition-colors opacity-0 group-hover:opacity-100">
                                View →
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Floating Bulk Action Bar */}
      {selected.size > 0 && (
        <div data-testid="bulk-action-bar"
          className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 flex items-center gap-2 bg-gray-900 text-white rounded-2xl px-4 py-3 shadow-2xl border border-gray-700">
          <span className="text-sm font-bold text-gray-100 whitespace-nowrap pr-2 border-r border-gray-700">
            {selected.size} selected
          </span>
          <Select value={bulkStatus} onValueChange={setBulkStatus}>
            <SelectTrigger className="w-44 h-8 text-xs bg-gray-800 border-gray-700 text-white hover:bg-gray-700" data-testid="bulk-status-select">
              <SelectValue placeholder="Set status..." />
            </SelectTrigger>
            <SelectContent>
              {ALL_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button size="sm" onClick={handleBulkStatus} disabled={!bulkStatus || bulkLoading} data-testid="bulk-update-btn"
            className="bg-lime-600 hover:bg-lime-500 text-white h-8 text-xs px-3 font-bold disabled:opacity-40 whitespace-nowrap">
            Update
          </Button>
          <div className="w-px h-5 bg-gray-700 mx-0.5" />
          <Button size="sm" onClick={handleExportSelected} disabled={bulkLoading} data-testid="export-selected-btn"
            className="bg-emerald-700 hover:bg-emerald-600 text-white h-8 text-xs px-3 font-bold">
            <FileSpreadsheet className="w-3.5 h-3.5 mr-1" /> Excel
          </Button>
          <Button size="sm" onClick={handleBulkPayment} disabled={bulkLoading} data-testid="bulk-paid-btn"
            className="bg-lime-700 hover:bg-lime-600 text-white h-8 text-xs px-3 font-bold">
            <CreditCard className="w-3.5 h-3.5 mr-1" /> Paid
          </Button>
          <Button size="sm" onClick={handleBulkDelete} disabled={bulkLoading} data-testid="delete-selected-btn"
            className="bg-red-700 hover:bg-red-600 text-white h-8 text-xs px-3 font-bold">
            <Trash2 className="w-3.5 h-3.5 mr-1" /> Delete
          </Button>
          <button onClick={() => { setSelected(new Set()); setBulkStatus(""); }} data-testid="clear-selection-btn"
            className="text-gray-400 hover:text-white transition-colors ml-1">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}
    </div>
  );
}
